import "./groupCode.css";
import React, { useEffect, useRef, useState } from "react";
import { Label, Input } from "@bootstrap-styled/v4";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignInAlt, faSpinner } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { request } from "hooks/useOkoApi/useOkoApi";
import { initiateGoogleSignIn, GOOGLE_OAUTH_STATE_KEY } from "utils/googleAuth";
import { initiateCleverSignIn, CLEVER_OAUTH_STATE_KEY } from "utils/cleverAuth";
import { initiateClassLinkSignIn, CLASSLINK_OAUTH_STATE_KEY } from "utils/classlinkAuth";
import { initiateMicrosoftSignIn, MICROSOFT_OAUTH_STATE_KEY } from "utils/microsoftAuth";
import { colors, constants, removeAllWhitespace, logError, logSession, LogCategory, LogLevel } from "utils";
import { REDIRECT_ERROR } from "components/pages/Session/sessionPrefetch";
import { saveSessionState, clearSessionState, SESSION_PHASE } from "utils/sessionPersistence";
import { getNetworkInfo } from "./sessionPrefetch";
import { IsolationLayout } from "components/shared/IsolationLayout/IsolationLayout";
import { Alert, Form, Button } from "react-bootstrap";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { OKOFooter } from "../../OKOFooter";

const REDIRECT_ERROR_MESSAGES = {
  [REDIRECT_ERROR.RETRY_FAILURE]: "We're having trouble getting you in! Double-check your group code and try again. Still stuck? Ask your teacher for help or try restarting your computer.",
  [REDIRECT_ERROR.SESSION_NOT_FOUND]: "We haven't found your team yet. Check your group code with your teammates to make sure it matches exactly, then try again. If you're still alone, ask your teacher for a hand!",
  [REDIRECT_ERROR.SESSION_INIT_FAILED]: "Your session couldn't start. Enter your group code to try again.",
};

const getInitialError = (timeout) => {
  const params = new URLSearchParams(window.location.search);
  const authError = params.get("auth_error");
  if (authError) return authError;
  const redirectError = params.get("error");
  if (redirectError) {
    if (REDIRECT_ERROR_MESSAGES[redirectError]) return REDIRECT_ERROR_MESSAGES[redirectError];
    // Unknown error type — render the raw value as a fallback. The full error
    // event is emitted from the useRedirectErrorLogging effect below.
    return redirectError;
  }
  if (timeout) return "Did you enter the right group code?";
  return null;
};

/**
 * Emits a single ERROR-level `session_redirect_error` event when the user
 * lands on GroupCode via `?error=…`. Captures every diagnostic we still have
 * after the redirect:
 *   - the error type (retry_failure / session_not_found / <unknown>)
 *   - the source step where the failure originated (e.g. "session_join",
 *     "rehydration", "ready_room_vote") — passed via ?error_source=
 *   - session id, session status, HTTP status code (when the caller had them)
 *   - identity (firebaseUserId, okoUserId) once auth resolves
 *   - the group code the user was trying to reach
 *   - auth-related error, if any
 *   - network + userAgent
 *
 * Gated on authUserData so okoUserId has a chance to populate before we log;
 * refs prevent double-firing.
 */
const useRedirectErrorLogging = (authUserData) => {
  const loggedRef = useRef(false);
  useEffect(() => {
    if (loggedRef.current) return;
    if (!authUserData) return;

    const params = new URLSearchParams(window.location.search);
    const redirectError = params.get("error");
    const authError = params.get("auth_error");
    if (!redirectError && !authError) return;

    loggedRef.current = true;

    const errorSource = params.get("error_source") || null;
    const errorSessionId = params.get("error_session_id") || null;
    const errorSessionStatus = params.get("error_session_status") || null;
    const errorStatusCodeRaw = params.get("error_status_code");
    const errorStatusCode = errorStatusCodeRaw !== null ? Number(errorStatusCodeRaw) : null;
    const groupCode = params.get("code") || null;
    const isKnownType = redirectError && Object.prototype.hasOwnProperty.call(REDIRECT_ERROR_MESSAGES, redirectError);

    logError(
      "Session redirect error reached GroupCode",
      new Error(`Redirected to GroupCode with error=${redirectError ?? authError}`),
      {
        category: LogCategory.SESSION,
        redirectError: redirectError || null,
        authError: authError || null,
        isKnownRedirectType: !!isKnownType,
        errorSource,
        errorSessionId,
        errorSessionStatus,
        errorStatusCode,
        groupCode,
        firebaseUserId: authUserData?.id ?? null,
        okoUserId: authUserData?.okoUserId ?? null,
        isAnonymous: authUserData?.isAnonymous ?? null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        ...getNetworkInfo(),
      }
    );
  }, [authUserData]);
};

export const GroupCode = ({ timeout }) => {
  const { authUserData } = useAuthUserData();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(getInitialError(timeout));
  useRedirectErrorLogging(authUserData);
  const [code, setCode] = useState(new URLSearchParams(window.location.search).get("code"));
  const [name, setName] = useState();
  const [authOptions, setAuthOptions] = useState();
  const { setGroupCode, setDisplayName, setSessionAuthType, setIsRecordSessions } = useAppStateContext();
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [isCodeTypedManually, setIsCodeTypedManually] = useState(false);

  // Clear persisted session when user lands on GroupCode without a code param or OAuth state
  // This allows a fresh start when manually navigating to /
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasCode = params.get("code");
    const hasOAuth = localStorage.getItem(GOOGLE_OAUTH_STATE_KEY) || localStorage.getItem(CLEVER_OAUTH_STATE_KEY) || localStorage.getItem(CLASSLINK_OAUTH_STATE_KEY) || localStorage.getItem(MICROSOFT_OAUTH_STATE_KEY);
    if (!hasCode && !hasOAuth && !timeout) {
      clearSessionState();
    }
  }, [timeout]);

  // This effect runs only once on component mount to handle OAuth redirect and initial group code
  useEffect(() => {
    // This is purely for cosmetic purposes, so we don't carry /timeout in the address bar
    if (timeout) window.history.replaceState({}, document.title, window.location.origin);
    if (!code || !authUserData || initialCheckDone || isCodeTypedManually) return;
    const handleInitialLoad = async () => {
      try {
        setIsLoading(true);
        // Check if we're returning from OAuth (Google, Clever, or ClassLink)
        const googleOauthState = localStorage.getItem(GOOGLE_OAUTH_STATE_KEY);
        const cleverOauthState = localStorage.getItem(CLEVER_OAUTH_STATE_KEY);
        const classlinkOauthState = localStorage.getItem(CLASSLINK_OAUTH_STATE_KEY);
        const microsoftOauthState = localStorage.getItem(MICROSOFT_OAUTH_STATE_KEY);

        console.log("[oko-patter] handleInitialLoad — urlCode:", code, "googleOAuth:", !!googleOauthState, "cleverOAuth:", !!cleverOauthState, "classlinkOAuth:", !!classlinkOauthState, "microsoftOAuth:", !!microsoftOauthState);

        if (googleOauthState) {
          try {
            const parsedState = JSON.parse(googleOauthState);
            console.log("[oko-patter] Google OAuth state — storedCode:", parsedState?.groupCode, "urlCode:", code, "match:", parsedState?.groupCode === code);
            // Stale OAuth state from a different session — discard and start fresh
            if (parsedState?.groupCode && parsedState.groupCode !== code) {
              localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            // If we have authType in the state, set it
            } else if (!authUserData.isAnonymous && parsedState?.authMethods?.includes(constants.GOOGLE)) {
              // Clear the state after using it
              localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
              setSessionAuthType(constants.GOOGLE);

              // Always use URL code as source of truth
              console.log("[oko-patter] Group code selected (Google OAuth):", code);
              setGroupCode(code);
              setAuthOptions(parsedState?.authMethods);
              setIsRecordSessions(parsedState?.recordSessions);
              saveSessionState({
                groupCode: code,
                displayName: authUserData?.displayName,
                sessionAuthType: constants.GOOGLE,
                isRecordSessions: parsedState?.recordSessions,
                phase: SESSION_PHASE.TECH_CHECK,
              });
            // User can be Google-authenticated and still use anonymous-enabled group code
            } else if (authUserData.isAnonymous) {
              localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            }
          } catch (e) {
            localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
            console.error("Error parsing Google oauth state:", e);
            logError("Failed to parse Google OAuth state", e, {
              category: LogCategory.AUTH,
              groupCode: code,
            });
          }
        } else if (cleverOauthState) {
          try {
            const parsedState = JSON.parse(cleverOauthState);
            console.log("[oko-patter] Clever OAuth state — storedCode:", parsedState?.groupCode, "urlCode:", code, "match:", parsedState?.groupCode === code);
            // Stale OAuth state from a different session — discard and start fresh
            if (parsedState?.groupCode && parsedState.groupCode !== code) {
              localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            // If we have authType in the state, set it
            } else if (!authUserData.isAnonymous && parsedState?.authMethods?.includes(constants.CLEVER)) {
              // Clear the state after using it
              localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
              setSessionAuthType(constants.CLEVER);

              // Always use URL code as source of truth
              console.log("[oko-patter] Group code selected (Clever OAuth):", code);
              setGroupCode(code);
              setAuthOptions(parsedState?.authMethods);
              setIsRecordSessions(parsedState?.recordSessions);
              saveSessionState({
                groupCode: code,
                displayName: authUserData?.displayName,
                sessionAuthType: constants.CLEVER,
                isRecordSessions: parsedState?.recordSessions,
                phase: SESSION_PHASE.TECH_CHECK,
              });
            // User can be Clever-authenticated and still use anonymous-enabled group code
            } else if (authUserData.isAnonymous) {
              localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            }
          } catch (e) {
            localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
            console.error("Error parsing Clever oauth state:", e);
            logError("Failed to parse Clever OAuth state", e, {
              category: LogCategory.AUTH,
              groupCode: code,
            });
          }
        } else if (classlinkOauthState) {
          try {
            const parsedState = JSON.parse(classlinkOauthState);
            console.log("[oko-patter] ClassLink OAuth state — storedCode:", parsedState?.groupCode, "urlCode:", code, "match:", parsedState?.groupCode === code);
            if (parsedState?.groupCode && parsedState.groupCode !== code) {
              localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            } else if (!authUserData.isAnonymous && parsedState?.authMethods?.includes(constants.CLASSLINK)) {
              localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
              setSessionAuthType(constants.CLASSLINK);

              console.log("[oko-patter] Group code selected (ClassLink OAuth):", code);
              setGroupCode(code);
              setAuthOptions(parsedState?.authMethods);
              setIsRecordSessions(parsedState?.recordSessions);
              saveSessionState({
                groupCode: code,
                displayName: authUserData?.displayName,
                sessionAuthType: constants.CLASSLINK,
                isRecordSessions: parsedState?.recordSessions,
                phase: SESSION_PHASE.TECH_CHECK,
              });
            } else if (authUserData.isAnonymous) {
              localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            }
          } catch (e) {
            localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
            console.error("Error parsing ClassLink oauth state:", e);
            logError("Failed to parse ClassLink OAuth state", e, {
              category: LogCategory.AUTH,
              groupCode: code,
            });
          }
        } else if (microsoftOauthState) {
          try {
            const parsedState = JSON.parse(microsoftOauthState);
            console.log("[oko-patter] Microsoft OAuth state — storedCode:", parsedState?.groupCode, "urlCode:", code, "match:", parsedState?.groupCode === code);
            if (parsedState?.groupCode && parsedState.groupCode !== code) {
              localStorage.removeItem(MICROSOFT_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            } else if (!authUserData.isAnonymous && parsedState?.authMethods?.includes(constants.MICROSOFT)) {
              localStorage.removeItem(MICROSOFT_OAUTH_STATE_KEY);
              setSessionAuthType(constants.MICROSOFT);

              console.log("[oko-patter] Group code selected (Microsoft OAuth):", code);
              setGroupCode(code);
              setAuthOptions(parsedState?.authMethods);
              setIsRecordSessions(parsedState?.recordSessions);
              saveSessionState({
                groupCode: code,
                displayName: authUserData?.displayName,
                sessionAuthType: constants.MICROSOFT,
                isRecordSessions: parsedState?.recordSessions,
                phase: SESSION_PHASE.TECH_CHECK,
              });
            } else if (authUserData.isAnonymous) {
              localStorage.removeItem(MICROSOFT_OAUTH_STATE_KEY);
              await handleSubmitGroupCode();
            }
          } catch (e) {
            localStorage.removeItem(MICROSOFT_OAUTH_STATE_KEY);
            console.error("Error parsing Microsoft oauth state:", e);
            logError("Failed to parse Microsoft OAuth state", e, {
              category: LogCategory.AUTH,
              groupCode: code,
            });
          }
        } else {
          // Not returning from OAuth, proceed with normal flow
          await handleSubmitGroupCode();
        }
      } catch (e) {
        logError("Failed to handle initial group code load", e, {
          category: LogCategory.SESSION,
          groupCode: code,
        });
        setError(e);
      } finally {
        setIsLoading(false);
        setInitialCheckDone(true);
      }
    };

    handleInitialLoad();
  }, [authUserData, code, initialCheckDone]);

  const handleSubmitDisplayName = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!name) {
      setError({ detail: "Name can't be empty!"});
      return;
    }
    setIsLoading(true);
    console.log("[oko-patter] Group code selected (anonymous):", code);
    setDisplayName(name);
    setSessionAuthType(constants.ANONYMOUS);
    saveSessionState({
      groupCode: code,
      displayName: name,
      sessionAuthType: constants.ANONYMOUS,
      isRecordSessions: undefined,
      phase: SESSION_PHASE.TECH_CHECK,
    });
  };

  const handleSubmitGroupCode = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!code) {
      setError({ detail: "Group code can't be empty!"});
      return;
    }
    console.log("[oko-patter] handleSubmitGroupCode — code:", code);
    setGroupCode(code);

    try {
      const authOptionsResponse = await request(`group/${code}/settings`, { method: "GET" });
      setAuthOptions(authOptionsResponse?.auth_methods);
      setIsRecordSessions(authOptionsResponse?.record_sessions);

      const redirectUri = `${window.location.origin}/?code=${code}`;
      const additionalState = {
        groupCode: code,
        authMethods: authOptionsResponse?.auth_methods,
        recordSessions: authOptionsResponse?.record_sessions,
      };

      // Check for Google authentication
      if (authOptionsResponse?.auth_methods.includes(constants.GOOGLE)) {
        await initiateGoogleSignIn(redirectUri, additionalState)
          .then(({ success, error }) => {
            if (!success) {
              setError(error);
              setIsLoading(false);
            }
            // If successful, the page will redirect, so the code below won't execute
          });
      }
      // Check for Clever authentication
      else if (authOptionsResponse?.auth_methods.includes(constants.CLEVER)) {
        await initiateCleverSignIn(redirectUri, additionalState)
          .then(({ success, error }) => {
            if (!success) {
              setError(error);
              setIsLoading(false);
            }
            // If successful, the page will redirect, so the code below won't execute
          });
      }
      // Check for ClassLink authentication
      else if (authOptionsResponse?.auth_methods.includes(constants.CLASSLINK)) {
        await initiateClassLinkSignIn(redirectUri, additionalState)
          .then(({ success, error }) => {
            if (!success) {
              setError(error);
              setIsLoading(false);
            }
            // If successful, the page will redirect, so the code below won't execute
          });
      }
      // Check for Microsoft authentication
      else if (authOptionsResponse?.auth_methods.includes(constants.MICROSOFT)) {
        await initiateMicrosoftSignIn(redirectUri, additionalState)
          .then(({ success, error }) => {
            if (!success) {
              setError(error);
              setIsLoading(false);
            }
            // If successful, microsoftAuth.js navigates to redirectUri (popup
            // flow); the resulting reload re-enters handleInitialLoad and is
            // finalized by the MICROSOFT_OAUTH_STATE_KEY branch above.
          });
      }
    } catch (e) {
      const isNetworkError = e instanceof TypeError && /Failed to fetch|NetworkError|Load failed/i.test(e.message);
      const isOffline = !navigator.onLine;
      const errorStatus = Number(e?.status) || Number(e?.statusCode);
      const is4xx = errorStatus >= 400 && errorStatus < 500;
      const is5xx = errorStatus >= 500 && errorStatus < 600;

      let reason;
      if (errorStatus === 404) reason = "not_found";
      else if (is4xx) reason = "rejected";
      else if (is5xx) reason = "server_error";
      else if (isOffline) reason = "offline";
      else if (isNetworkError) reason = "network";
      else reason = "unknown";

      // 4xx (incl. 404 "invalid code") and offline are expected user-level conditions,
      // not system errors. 5xx / unexpected network failures are the ones worth paging on.
      const level = (is4xx || isOffline) ? LogLevel.WARNING : LogLevel.ERROR;

      logSession("Failed to submit group code", {
        level,
        groupCode: code,
        reason,
        endpoint: "group/{code}/settings",
        errorStatus: errorStatus || undefined,
        errorDetail: e?.detail,
        errorMessage: e?.message,
        errorStack: e?.stack,
        retryCount: e?.retryCount,
        isNetworkError,
        isOffline,
        ...getNetworkInfo(),
      });

      if (isNetworkError && isOffline) {
        setError({
          detail: "You appear to be offline. Please check your internet connection and try again.",
        });
      } else {
        setError(e);
      }
      setIsLoading(false);
    }
  };

  const labelStyle = { color: "#1D1F23" };
  const inputStyle = { fontSize: "16px", padding: "0 12px", width: "205px", height: "40px" };
  const buttonStyle = {
    backgroundColor: colors.pink700,
    color: "#fff",
    border: "none",
    borderRadius: "20px",
    fontSize: "16px",
    marginTop: "32px",
    width: "100%",
    height: "48px" };

  return (
    isLoading ? (
      <div style={{
        color: "white",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: "10"
      }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>
    ) : (
      <>
        <IsolationLayout>
          <ContentContainer>
            <p style={{ fontSize: "48px", fontWeight: 500, position: "relative", width: "205px", height: "56px" }}>
              Hello
            </p>
            {!authOptions && <Form className="drop-in" onSubmit={handleSubmitGroupCode}>
              <Label className="mb-1" style={labelStyle}>
                Enter your group code
              </Label>
              <Input
                autoFocus
                placeholder="Group code"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                size="lg"
                style={inputStyle}
                value={code || ""}
                onChange={(e) => {
                  setIsCodeTypedManually(true);
                  setCode(removeAllWhitespace(e.target.value));
                }}
              />
              <Button
                type="submit"
                size="lg"
                style={buttonStyle}>
                Submit <FontAwesomeIcon icon={faSignInAlt} className="ml-2" />
              </Button>
            </Form>}
            {authOptions?.includes(constants.ANONYMOUS) && <Form className="drop-in" onSubmit={handleSubmitDisplayName}>
              <Label className="mb-1" style={labelStyle}>
                Enter your name
              </Label>
              <Input
                autoFocus
                maxLength={32}
                placeholder="Name"
                size="lg"
                style={{ ...inputStyle, width: "258px" }}
                value={name || ""}
                onChange={(e) => setName(e.target.value)}
              />
              <p style={{ fontSize: "12px", marginTop: "4px" }}>
                Note: Your teacher can see the name you submit
              </p>
              <Button
                type="submit"
                size="lg"
                style={buttonStyle}>
                Join <FontAwesomeIcon icon={faSignInAlt} className="ml-2" />
              </Button>
              {/* {code && (
                <GroupCodeLabel>Group Code: {code}</GroupCodeLabel>
              )} */}
            </Form>}
            {error && (
              <div style={{
                position: "absolute",
                bottom: "-100px",
                left: "50%",
                transform: "translateX(-50%)",
                minWidth: "700px",
                maxHeight: "150px",
                zIndex: "5",
                textAlign: "center",
                textOverflow: "ellipsis",
                overflow: "auto"
              }}>
                <Alert dismissible
                       variant="danger"
                       style={{ overflowWrap: "break-word", width: "auto", display: "inline-block", marginBottom: 0 }}
                       onClose={() => setError(null)}>
                  {error?.detail || error?.message || error || "An unknown error occurred."}
                </Alert>
              </div>
            )}
          </ContentContainer>
        </IsolationLayout>
        <div style={{ position: "absolute", bottom: "0", width: "100%", zIndex: "5" }}>
          <OKOFooter />
        </div>
      </>
    )
  );
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  z-index: 2;
  position: relative;
  color: ${colors.nightlyWoods950};
  margin-top: -32px;
  margin-left: -32px;
`;

const GroupCodeLabel = styled.p`
  font-size: 14px;
  color: ${colors.milkGlass950};
  margin: 10px 0 0 0;
`;
